/** Copy to the clipboard, in every shell we ship (2026-09-10).
 *
 * The async Clipboard API is not available everywhere the app runs: in the Teacher Computer
 * app's webview `navigator.clipboard.writeText` rejects with "not allowed by the user agent"
 * (measured in the built shell), so the one call site we had -- the calendar feed link --
 * threw inside its click handler and the teacher saw nothing at all happen.
 *
 * So: ask the modern API first, fall back to the selection-and-`execCommand` path that
 * WebKit still honours inside a click, and tell the caller which of the two happened. A
 * caller that gets `false` must show the text instead of pretending it copied.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // The desktop webview refuses it; the legacy path below is the one that works there.
  }
  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    // Off-screen but focusable: `display: none` and `hidden` cannot be selected.
    field.style.position = "fixed";
    field.style.top = "0";
    field.style.opacity = "0";
    field.style.pointerEvents = "none";
    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    field.remove();
    return ok;
  } catch {
    return false;
  }
}
